import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../api/client';
import type {
  CreateManualJournalEntryRequest,
  JournalEntryDto,
  JournalPageDto,
} from './types';

const QK_ROOT = ['journal'] as const;

export interface JournalFilters {
  from: string | null;
  to: string | null;
  source: number | null;
  page: number;
}

export function useJournal(filters: JournalFilters) {
  return useQuery({
    queryKey: [...QK_ROOT, filters] as const,
    queryFn: async () =>
      (
        await apiClient.get<JournalPageDto>('/api/accounting/journal', {
          params: {
            from: filters.from ?? undefined,
            to: filters.to ?? undefined,
            source: filters.source ?? undefined,
            page: filters.page,
            pageSize: 50,
          },
        })
      ).data,
  });
}

export function useJournalEntry(id: string | null) {
  return useQuery({
    queryKey: [...QK_ROOT, 'entry', id] as const,
    enabled: id !== null,
    queryFn: async () =>
      (await apiClient.get<JournalEntryDto>(`/api/accounting/journal/${id}`)).data,
  });
}

export function useCreateManualEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (request: CreateManualJournalEntryRequest) =>
      (await apiClient.post<JournalEntryDto>('/api/accounting/journal', request)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: QK_ROOT }),
  });
}
